import React, { useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Tooltip } from '@material-ui/core';
import { notify } from 'react-notify-toast';
import Select, { components } from 'react-select';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
// Components
import { SaveButton } from '../save-button/save-button';
import { CancelButton } from '../cancel-button/cancel-button';
// Store
import { updateProject } from '@united-talent-agency/julius-frontend-store';
// Constants
import { PLACEMENT_CATEGORIES, SELECT_STYLES, CARD_STYLES } from '../../support/items/placements';

/**
 * ClearIndicator - Custom clear indicator with tooltip
 */
const ClearIndicator = props => (
  <Tooltip title="Clear all">
    <div>
      <components.ClearIndicator {...props} />
    </div>
  </Tooltip>
);

const PlacementsCard = ({ project = {}, dispatch }) => {
  const { isPlacement } = useFlags();
  if (!isPlacement) return null;

  const TITLE = 'Product Placement';

  const [isEditing, setIsEditing] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);

  // Transform base options
  const placementCategorySelectOptions = useMemo(
    () =>
      PLACEMENT_CATEGORIES.map(option => ({
        label: option,
        value: option,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  // Add "Select All" at the top
  const optionsWithSelectAll = useMemo(() => {
    const allSelected = selectedCategories.length === placementCategorySelectOptions.length;

    return [
      {
        label: 'Select All',
        value: '__all__',
        isDisabled: allSelected, // disable if everything is already selected
      },
      ...placementCategorySelectOptions,
    ];
  }, [selectedCategories, placementCategorySelectOptions]);

  // Initialize state from project prop
  useEffect(() => {
    if (project?.placements?.length > 0) {
      const initialSelections = project.placements
        .map(placement => placementCategorySelectOptions.find(opt => opt.value === placement))
        .filter(Boolean);
      setSelectedCategories(initialSelections);
      setOriginalCategories(initialSelections);
    } else {
      setSelectedCategories([]);
      setOriginalCategories([]);
    }
  }, [project, placementCategorySelectOptions]);

  /**
   * Event Handlers
   * - Memoized for performance
   * - Properly handle edge cases
   */
  const handlePlacementChange = useCallback(
    selectedOptions => {
      if (!selectedOptions) {
        setSelectedCategories([]);
        setIsEditing(originalCategories.length > 0);
        return;
      }

      // Handle "Select All"
      if (selectedOptions.some(option => option.value === '__all__')) {
        setSelectedCategories(placementCategorySelectOptions);
        setIsEditing(true);
        return;
      }

      setSelectedCategories(selectedOptions);

      const hasChanges =
        selectedOptions.length !== originalCategories.length ||
        selectedOptions.some(item => !originalCategories.some(orig => orig.value === item.value));

      setIsEditing(hasChanges);
    },
    [originalCategories, placementCategorySelectOptions]
  );

  const resetChanges = useCallback(() => {
    setSelectedCategories(originalCategories);
    setIsEditing(false);
  }, [originalCategories]);

  const handleSubmit = useCallback(async () => {
    try {
      const result = await dispatch(
        updateProject(project._id, {
          placements: selectedCategories.map(item => item.value),
        })
      );

      if (result.status !== 200) {
        throw new Error('Failed to save placement details');
      }

      // Update original categories to match the new saved state
      setOriginalCategories(selectedCategories);
      setIsEditing(false);
      notify.show(`${TITLE} Saved`, 'custom', 2000, CARD_STYLES.toast);
    } catch (error) {
      setIsEditing(false);
      notify.show('Error saving placement details', 'error', 2000, CARD_STYLES.toast);
      console.error('Placement save error:', error);
    }
  }, [dispatch, selectedCategories, project._id]);

  return (
    <div className="card rounded-0 border-white shadow-none mb-4 h-auto">
      <div
        className="px-3 py-2"
        style={isEditing ? CARD_STYLES.headerEdit : undefined}
        data-testid="placements-card-header"
      >
        <div
          className={classNames('d-flex justify-content-between align-items-center', {
            'border-bottom pb-2': !isEditing,
          })}
        >
          <div className="text-uppercase font-weight-light" style={CARD_STYLES.fontX}>
            {TITLE}
          </div>

          {isEditing && (
            <div className="d-flex align-items-center">
              <CancelButton testId="card-cancel-button" onClick={resetChanges} />
              <SaveButton testId="card-save-button" onClick={handleSubmit} />
            </div>
          )}
        </div>
      </div>

      <div className="d-flex flex-column justify-content-center align-items-center px-3 py-3 w-100">
        <div className="w-100">
          <Select
            isMulti
            isClearable
            captureMenuScroll={false}
            placeholder="Brand Categories"
            components={{ ClearIndicator }}
            value={selectedCategories}
            onChange={handlePlacementChange}
            options={optionsWithSelectAll}
            styles={SELECT_STYLES}
            isSearchable
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            isOptionDisabled={option => option.isDisabled} // respect disabled state
            aria-label="Brand categories select"
            data-testid="brand-categories-select"
          />
        </div>
      </div>
    </div>
  );
};

PlacementsCard.propTypes = {
  project: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    placements: PropTypes.arrayOf(PropTypes.string),
  }),
  dispatch: PropTypes.func.isRequired,
};

// Default props
PlacementsCard.defaultProps = {
  project: {},
};

export default React.memo(PlacementsCard);
